import { createQueryFactory } from '@/api';
import type { StringQueryCriteria } from '@/api/types';
import { queryOptions } from '@tanstack/react-query';

import { vesselJobsEndpoints, type ApprovalsCriteria, type FleetJobsCriteria } from './endpoints';
import type { VesselJobEx } from './schema';

/** Once-off jobs (no IntervalType) are only shown in Fleet Jobs dashboard (instance level). */
const excludeOnceOffJobs = (jobs: VesselJobEx[]) => jobs.filter((job) => !!job.IntervalType);

export const vesselJobsQueries = {
  ...createQueryFactory('vesselJobs', vesselJobsEndpoints),

  byVesselComponent: (vesselComponentId: string, criteria?: StringQueryCriteria) =>
    queryOptions({
      queryKey: ['vesselJobs', 'list', 'byVesselComponent', vesselComponentId, criteria] as const,
      queryFn: () => vesselJobsEndpoints.getVesselJobs({ ...criteria, VesselComponentId: vesselComponentId }),
      select: excludeOnceOffJobs,
      enabled: !!vesselComponentId
    }),

  byAbstractComponent: (abstractComponentId: string, vesselId: string, criteria?: StringQueryCriteria) =>
    queryOptions({
      queryKey: ['vesselJobs', 'list', 'byAbstractComponent', abstractComponentId, vesselId, criteria] as const,
      queryFn: () =>
        vesselJobsEndpoints.getVesselJobs({
          ...criteria,
          VesselComponentAbstractComponentId: abstractComponentId,
          VesselId: vesselId
        }),
      select: excludeOnceOffJobs,
      enabled: !!abstractComponentId && !!vesselId
    }),

  byVessel: (vesselId: string, criteria?: StringQueryCriteria) =>
    queryOptions({
      queryKey: ['vesselJobs', 'list', 'byVessel', vesselId, criteria] as const,
      queryFn: () => vesselJobsEndpoints.getVesselJobs({ ...criteria, VesselId: vesselId }),
      select: excludeOnceOffJobs,
      enabled: !!vesselId
    }),

  fleetDashboard: (criteria?: FleetJobsCriteria) =>
    queryOptions({
      queryKey: ['vesselJobs', 'list', 'fleetDashboard', criteria] as const,
      queryFn: () => vesselJobsEndpoints.getFleetJobInstances(criteria)
    }),

  fleetJobsKpi: (criteria?: FleetJobsCriteria) =>
    queryOptions({
      queryKey: ['vesselJobs', 'kpi', criteria] as const,
      queryFn: () => vesselJobsEndpoints.getFleetJobsKpi(criteria)
    }),

  /** Prefix key for all approval queries. Used by invalidateQueries to bust the entire approvals cache.
   *  All approval queryKeys MUST start with this prefix. */
  approvalsAll: () => ['vesselJobs', 'approvals'] as const,

  approvalsDashboard: (criteria?: ApprovalsCriteria) =>
    queryOptions({
      queryKey: ['vesselJobs', 'approvals', criteria] as const,
      queryFn: () => vesselJobsEndpoints.getApprovals(criteria)
    })
};
